import { createSlice, current } from '@reduxjs/toolkit';
import { mediaInitialState } from './mediaInitialState';
import {
  BROWSER_ITEM_TYPES,
  MEDIAPANE_KEYS,
} from '../constants/WbMediaConstants';
import { getVisibleMediaIds } from '../services/mediaBrowserHelper';

const isMedia = (item) => item.type === BROWSER_ITEM_TYPES.MEDIA;

// Enforce the invariant "selection is always a subset of what is visible" at
// the source: whenever the visible set changes (folder navigation, folder
// collapse, the tree being replaced by a move/page-load, etc.) drop any
// selected media that is no longer visible. This keeps every consumer of
// itemSelected (delete, drag-move, the checkbox) safe without each one having
// to re-check visibility.
const pruneSelectionToVisible = (state, mediaSelectorKey) => {
  // Nothing selected -> nothing to prune. Bail before the visibility walk so
  // hot paths like page-append (setBrowserTree) don't pay for it while the user
  // is just browsing, and so an empty initial tree (no expandedNodes yet) is a
  // no-op rather than a needless full-tree rewrite.
  if (!state[mediaSelectorKey].browserTree.some((item) => item.itemSelected)) {
    return;
  }
  const slice = current(state[mediaSelectorKey]);
  const visibleIds = new Set(
    getVisibleMediaIds({
      browserTree: slice.browserTree,
      isGridView: slice.viewMode?.key === MEDIAPANE_KEYS.VIEW.GRID,
      isSearching: !!slice.searchText,
      folderNode: slice.folderNode,
      expandedNodes: slice.expandedNodes,
    }),
  );
  state[mediaSelectorKey].browserTree = slice.browserTree.map((item) => ({
    ...item,
    itemSelected: item.itemSelected && visibleIds.has(item.id),
  }));
};

export const mediaSlice = createSlice({
  name: 'media',
  initialState: mediaInitialState,
  reducers: {
    // `loading` is a reference count of in-flight media fetches, exposed to
    // consumers as a boolean. Fetches can overlap - a user-initiated
    // reset-search bypasses the loading guard so it is never dropped - and a
    // plain boolean let whichever fetch finished first clear the flag while
    // another was still running. That opened a gap where the scroll sentinel
    // (which fires on the loading true->false edge) issued a duplicate request
    // before the live fetch committed its results. Counting claims instead means
    // loading goes false only when the last fetch settles, while still
    // guaranteeing every claim is released so the flag cannot wedge.
    setLoadingMedia(state, action) {
      const { loading, mediaSelectorKey } = action.payload;
      const mediaState = state[mediaSelectorKey];
      const pending = mediaState.pendingFetches || 0;
      mediaState.pendingFetches = Math.max(0, pending + (loading ? 1 : -1));
      mediaState.loading = mediaState.pendingFetches > 0;
      if (loading) {
        mediaState.noResults = false;
      }
    },
    setLayouts(state, action) {
      const { layouts } = action.payload;
      state.layouts = layouts;
    },
    setMediaType(state, action) {
      const { mediaType, mediaSelectorKey } = action.payload;
      state[mediaSelectorKey].mediaType = mediaType;
    },
    setMediaTypeUpload(state, action) {
      const { mediaTypeUpload, mediaSelectorKey } = action.payload;
      state[mediaSelectorKey].mediaTypeUpload = mediaTypeUpload;
    },
    setSearchText(state, action) {
      const { searchText, mediaSelectorKey } = action.payload;
      state[mediaSelectorKey].searchText = searchText;
      // Entering or clearing a search swaps the result set the user sees, so any
      // selection that is no longer visible must not survive the transition.
      pruneSelectionToVisible(state, mediaSelectorKey);
    },
    setViewMode(state, action) {
      const { mediaSelectorKey, viewModeKey } = action.payload;
      const viewMode = mediaInitialState[mediaSelectorKey].paneViews.find(
        (pn) => pn.key === viewModeKey,
      );
      state[mediaSelectorKey].viewMode = viewMode;
      // Tree and grid views surface different subsets of the loaded media. Keep
      // any selected media that is still visible in the new view and drop the
      // rest, so a selection can never be hidden (and silently acted on) but the
      // user also doesn't lose selections for media they can still see.
      pruneSelectionToVisible(state, mediaSelectorKey);
    },
    setPaneView(state, action) {
      const { paneViewOption, mediaSelectorKey } = action.payload;
      state[mediaSelectorKey].paneViewOption = paneViewOption;
    },
    setPreviewMedia(state, action) {
      const { previewMedia, mediaSelectorKey } = action.payload;
      state[mediaSelectorKey].previewMedia = previewMedia;
    },
    setMultiSelect(state, action) {
      const { multiSelect, mediaSelectorKey } = action.payload;
      state[mediaSelectorKey].multiSelect = multiSelect;
    },
    setSelectAll(state, action) {
      const { status, mediaIds = [], mediaSelectorKey } = action.payload;
      const currentState = current(state);
      const { browserTree } = currentState[mediaSelectorKey];

      // "Select all" only ever selects the media the user can currently see
      // (the visible ids passed in). Everything else - folders, the root, and
      // any media outside the visible set - is forced unselected so a hidden
      // selection can never linger and be deleted.
      const visibleIds = new Set(mediaIds);
      const newBrowserTree = browserTree.map((item) => ({
        ...item,
        itemSelected: status && isMedia(item) && visibleIds.has(item.id),
      }));
      state[mediaSelectorKey].browserTree = newBrowserTree;
    },
    setMediaItemSelected(state, action) {
      const { mediaItem, mediaSelectorKey, checked } = action.payload;
      const currentState = current(state);
      const { browserTree } = currentState[mediaSelectorKey];
      const newBrowserTree = browserTree.map((item) => {
        if (item.id === mediaItem.id) {
          return { ...item, itemSelected: checked };
        }
        return item;
      });
      state[mediaSelectorKey].browserTree = newBrowserTree;
    },
    setNoResults(state, action) {
      const { noResults, mediaSelectorKey } = action.payload;
      state[mediaSelectorKey].noResults = noResults;
    },
    cleanMediaSlice(state, action) {
      const { mediaSelectorKey } = action.payload;
      const currentState = current(state);
      const { viewMode } = currentState[mediaSelectorKey];
      // Keep the view mode of the media selector key
      state[mediaSelectorKey] = {
        ...mediaInitialState[mediaSelectorKey],
        viewMode,
      };
    },
    cleanAllMediaSlice() {
      return mediaInitialState;
    },
    setFolderNode(state, action) {
      const { node, mediaSelectorKey } = action.payload;

      state[mediaSelectorKey].folderNode = node;
      pruneSelectionToVisible(state, mediaSelectorKey);
    },
    setBrowserTree(state, action) {
      const { browserTree, mediaSelectorKey } = action.payload;
      state[mediaSelectorKey].browserTree = browserTree;
      // Replacing the tree (move, page-load/append, folder add/delete) can make
      // previously selected media disappear from the current view; drop those
      // so a hidden selection can't be silently dragged or deleted later.
      pruneSelectionToVisible(state, mediaSelectorKey);
    },
    setMediaPagination(state, action) {
      const { pageSize, total, page, mediaSelectorKey, folderKey } =
        action.payload;

      state[mediaSelectorKey].mediaPagination[folderKey] = {
        pageSize,
        total,
        page,
      };
    },
    resetMediaPagination(state, action) {
      const { mediaSelectorKey } = action.payload;

      state[mediaSelectorKey].mediaPagination = {};
    },
    updatePaginationSize(state, action) {
      const { mediaSelectorKey, folderKey, total } = action.payload;

      state[mediaSelectorKey].mediaPagination[folderKey].total = total;
    },
    setIsAddingFolder(state, action) {
      const { isAddingFolder, mediaSelectorKey } = action.payload;

      state[mediaSelectorKey].isAddingFolder = isAddingFolder;
    },
    setFolderExpanded(state, action) {
      const { expandedNodes, mediaSelectorKey } = action.payload;

      state[mediaSelectorKey].expandedNodes = expandedNodes;
      pruneSelectionToVisible(state, mediaSelectorKey);
    },
  },
});

export const {
  cleanAllMediaSlice,
  cleanMediaSlice,
  resetMediaPagination,
  setFolderExpanded,
  setFolderNode,
  setBrowserTree,
  setIsAddingFolder,
  setLayouts,
  setLoadingMedia,
  setMediaItemSelected,
  setMediaPagination,
  setMediaType,
  setMediaTypeUpload,
  setMultiSelect,
  setNoResults,
  setPaneView,
  setPreviewMedia,
  setSelectAll,
  setSearchText,
  setViewMode,
  updatePaginationSize,
} = mediaSlice.actions;

export default mediaSlice.reducer;
